// src/components/CommandPalette/CommandPaletteContext.jsx
//
// Estado del command palette y el atajo global que lo abre. Vive por encima del
// router para que ⌘K funcione en toda la app autenticada — incluido el editor de
// workflows, que está fuera del AppShell y por tanto no tiene rail.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const CommandPaletteContext = createContext({ open: false, setOpen: () => {}, toggle: () => {} });

export const useCommandPalette = () => useContext(CommandPaletteContext);

// Mientras alguien escribe en un campo, ⌘K es suyo, no nuestro: robarlo rompe
// atajos del navegador y del sistema dentro de editores de texto.
function isTypingTarget(el) {
  if (!el) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

export function CommandPaletteProvider({ children }) {
  const [open, setOpen] = useState(false);
  // En qué paso abre el palette. `null` es la búsqueda de siempre; una acción
  // que necesita elegir algo antes de ejecutarse abre directamente en su paso.
  // Así "Create agent" desde Home y "New agent" desde ⌘K son la misma cosa y
  // no dos implementaciones que hay que acordarse de mantener iguales.
  const [page, setPage] = useState(null);
  // Al cerrar hay que devolver el foco donde estaba, o quien navega con teclado
  // se queda al principio del documento.
  const lastFocused = useRef(null);

  const openPalette = useCallback((options) => {
    // Se usa como `onClick={openPalette}` en varios sitios, así que lo que
    // llega puede ser un evento del DOM. Solo se acepta un objeto de opciones.
    const next = options && typeof options === "object" && !options.nativeEvent
      ? options.page ?? null
      : null;
    setPage(next);
    lastFocused.current = document.activeElement;
    setOpen(true);
  }, []);

  const closePalette = useCallback(() => {
    setOpen(false);
    setPage(null);
    const el = lastFocused.current;
    if (el && typeof el.focus === "function" && document.contains(el)) el.focus();
    lastFocused.current = null;
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      const isK = e.key === "k" || e.key === "K";
      if (!isK || !(e.metaKey || e.ctrlKey)) return;
      if (!open && isTypingTarget(document.activeElement)) return;
      e.preventDefault();
      if (open) closePalette();
      else openPalette();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, openPalette, closePalette]);

  const value = useMemo(
    () => ({
      open,
      page,
      setPage,
      openPalette,
      closePalette,
      setOpen: (next) => (next ? openPalette() : closePalette()),
    }),
    [open, page, openPalette, closePalette]
  );

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
}

/** "⌘K" en Mac, "Ctrl K" en el resto. Se muestra para que el atajo se descubra. */
export function shortcutLabel() {
  if (typeof navigator === "undefined") return "Ctrl K";
  const mac = /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");
  return mac ? "⌘K" : "Ctrl K";
}
